import { z } from "zod"
import { SuggestionChipSchema } from "./api.types"
import type { SuggestionChip, ToolCallBody } from "./api.types"

// Message sender types
export type MessageSender = "user" | "agent";

// Message types
export type MessageType = "text" | "widget" | "error" | "tool_call";

// Base message schema
export const BaseMessageSchema = z.object({
  id: z.string(),
  sender: z.enum(["user", "agent"]),
  timestamp: z.number(),
})

// Ordered display parts of an agent message: prose and widgets interleaved
// at the positions the server resolved (mirrors the API's message segments).
export const MessageSegmentSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), content: z.string() }),
  z.object({ type: z.literal("widget"), widgetType: z.string(), data: z.any() }),
])

// Text message schema. The message IS its ordered parts: prose and widgets
// interleaved as the server resolved them. Plain answers are a single text
// part; use messageText() where a flat string is needed.
export const TextMessageSchema = BaseMessageSchema.extend({
  type: z.literal("text"),
  segments: z.array(MessageSegmentSchema),
  serverId: z.string().optional(),
  wasHelpful: z.boolean().nullable().optional(),
  // Contextual follow-up chips, attached after the message via a `suggestions`
  // stream event (arrives a beat after `complete`).
  suggestions: z.array(SuggestionChipSchema).optional(),
  // True once a `text_delta` event arrived for this turn — the streaming-governor
  // path signature. Buffered turns (only `text`/`widget` events) leave it
  // undefined, so the UI renders them whole at `complete` instead of animating
  // an already-finished answer.
  streamed: z.boolean().optional(),
})

// Error message schema
export const ErrorMessageSchema = BaseMessageSchema.extend({
  type: z.literal("error"),
  content: z.string(),
  // Fallback chips (e.g. a Provider Directory link when a provider tool
  // fails), attached via a `suggestions` stream event that follows `error`.
  suggestions: z.array(SuggestionChipSchema).optional(),
})

// Tool call message schema (shown when agent is calling a tool)
export const ToolCallMessageSchema = BaseMessageSchema.extend({
  type: z.literal("tool_call"),
  display: z.string(),
})

// Combined message schema
export const MessageSchema = z.discriminatedUnion("type", [
  TextMessageSchema,
  ErrorMessageSchema,
  ToolCallMessageSchema,
])

// Inferred types from schemas
export type MessageSegment = z.infer<typeof MessageSegmentSchema>;
export type TextMessage = z.infer<typeof TextMessageSchema>;
export type ErrorMessage = z.infer<typeof ErrorMessageSchema>;
export type ToolCallMessage = z.infer<typeof ToolCallMessageSchema>;
export type Message = z.infer<typeof MessageSchema>;

// Chat state with thread management
export interface ChatState {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  threadId: string | null;
}

// Chat actions
export type ChatAction =
  | { type: "ADD_MESSAGE"; payload: Message }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "CLEAR_MESSAGES" }
  | { type: "SET_THREAD_ID"; payload: string | null }
  | {
      type: "SET_MESSAGE_VOTE";
      payload: { id: string; wasHelpful: boolean | null };
    }
  | {
      type: "SET_MESSAGE_SUGGESTIONS";
      payload: { serverId: string; chips: SuggestionChip[] };
    }
  // Streaming-with-governor: upsert the in-progress agent bubble's segments as
  // events arrive, then finalize it (attach serverId) on `complete`.
  | {
      type: "UPSERT_STREAMING_MESSAGE";
      payload: { id: string; segments: MessageSegment[]; streamed?: boolean };
    }
  | {
      type: "FINALIZE_STREAMING_MESSAGE";
      payload: { id: string; serverId?: string };
    }
  // Drop an in-progress streaming bubble that was never finalized (client abort,
  // or `complete` arrived with empty content and the error card replaces it).
  | { type: "REMOVE_MESSAGE"; payload: { id: string } };

// Flat text of a message's prose parts (tracking, empty checks).
export function messageText(segments: MessageSegment[]): string {
  return segments
    .filter((segment) => segment.type === "text")
    .map((segment) => segment.content)
    .join("\n\n")
}

// Utility function to create messages
export function createUserMessage(content: string): TextMessage {
  return {
    id: crypto.randomUUID(),
    sender: "user",
    type: "text",
    segments: [{ type: "text", content }],
    timestamp: Date.now(),
  }
}

export function createAgentTextMessage(
  segments: MessageSegment[],
  serverId?: string,
): TextMessage {
  return {
    id: crypto.randomUUID(),
    sender: "agent",
    type: "text",
    segments,
    timestamp: Date.now(),
    serverId,
    wasHelpful: undefined,
  }
}

export function createErrorMessage(content: string): ErrorMessage {
  return {
    id: crypto.randomUUID(),
    sender: "agent",
    type: "error",
    content,
    timestamp: Date.now(),
  }
}

export function createToolCallMessage(toolCall: ToolCallBody): ToolCallMessage {
  return {
    id: crypto.randomUUID(),
    sender: "agent",
    type: "tool_call",
    display: toolCall.display,
    timestamp: Date.now(),
  }
}
